import { GraduationCap } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CreatorCompletedMasterclassesItem } from "@/components/crew/creator-completed-masterclasses-item"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { CompletedMasterclassesPayload } from "@/types/crew"

/**
 * OKS-57 — Per-creator Masterclasses-completed card
 * (`Brand/Crew/Show`, left column, after `<StatusHistoryTimeline>`).
 *
 * Reads the OKS-57 `completed_masterclasses` Inertia prop. UX Spec §3
 * states branched here:
 *  - 0 completions + brand has ≥1 published → "No masterclasses completed yet."
 *  - 0 completions + brand has 0 published → "No training published yet."
 *  - ≥1 completion → count chip + items list (each linking to the
 *    masterclass Show page; "Unpublished" badge inline for unpublished
 *    completed rows — TRD KD-8 always-link).
 *
 * Count chip semantics:
 *  - When `total_published > 0`: "{count}/{total_published}"
 *  - When `total_published == 0` AND `count == 0`: chip suppressed (empty
 *    branch covers it)
 *
 * Server-side sort (`completed_at DESC`; TRD KD-5) — FE never re-sorts.
 *
 * Accessibility: items render as `<a>` semantic links inside a real
 * `<ul>`/`<li>`; count chip carries the visible label only (no extra
 * `aria-label` — the chip is decorative shorthand of the section title).
 */
export interface CreatorCompletedMasterclassesSectionProps {
  payload: CompletedMasterclassesPayload
}

export function CreatorCompletedMasterclassesSection({
  payload,
}: CreatorCompletedMasterclassesSectionProps) {
  const { t } = useTranslation()

  const { count, total_published, items } = payload

  const hasItems = count > 0
  const hasLibrary = total_published > 0

  // Count chip — visible only when the section has items. The denominator
  // is dropped when `total_published === 0` (defensive — a creator can't
  // have completions against zero published masterclasses unless those
  // completions reflect an unpublish, in which case the chip with `0`
  // would read incorrectly).
  const countChipText = hasLibrary
    ? t("crew.detail.masterclasses.count_chip", {
        count,
        total: total_published,
      })
    : t("crew.detail.masterclasses.count_chip_only", { count })

  return (
    <Card data-testid="creator-completed-masterclasses-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>{t("crew.detail.masterclasses.title")}</CardTitle>
        {hasItems && (
          <Badge
            variant="secondary"
            data-testid="creator-completed-masterclasses-count-chip"
          >
            {countChipText}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {hasItems ? (
          <ul
            data-testid="creator-completed-masterclasses-list"
            className="flex flex-col"
          >
            {items.map((item) => (
              <CreatorCompletedMasterclassesItem key={item.id} item={item} />
            ))}
          </ul>
        ) : (
          <div
            data-testid="creator-completed-masterclasses-empty"
            data-state={hasLibrary ? "no-completions" : "no-published"}
            className="flex flex-col items-center gap-2 border-t border-dashed py-8 text-center"
          >
            <GraduationCap
              className="text-muted-foreground size-7"
              aria-hidden="true"
            />
            <p className="text-muted-foreground text-sm">
              {hasLibrary
                ? t("crew.detail.masterclasses.empty_no_completions")
                : t("crew.detail.masterclasses.empty_no_published")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
